import * as d3 from "d3";
/**
 * Return AM/PM labels for given locale. When the locale does not use period
 * labels, empty strings will be returned.
 * @param locale THe locale to use
 * @returns The labels for the period of the day for given locale
 */
export const getPeriodLabels = (locale) => {
    const periods = ["", ""];
    const df = Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        timeStyle: "medium",
    });
    const date = new Date(Date.UTC(2000, 0, 2, 2, 0, 0, 0));
    const getDayPeriod = (parts) => {
        const dayPeriods = parts.filter((part) => part.type === "dayPeriod");
        return dayPeriods && dayPeriods.length > 0 ? dayPeriods[0].value : "";
    };
    let periodIndex = 0;
    for (let i = 2; i < 15; i += 12) {
        date.setHours(i);
        const parts = df.formatToParts(date);
        periods[periodIndex++] = getDayPeriod(parts);
    }
    return periods;
};
export const getMonthLabels = (locale) => {
    const months = {
        long: ["", "", "", "", "", "", "", "", "", "", "", ""],
        short: ["", "", "", "", "", "", "", "", "", "", "", ""],
        narrow: ["", "", "", "", "", "", "", "", "", "", "", ""],
    };
    const df = Intl.DateTimeFormat(locale, { timeZone: "UTC", month: "long" });
    const dfShort = Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        month: "short",
    });
    const dfNarrow = Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        month: "narrow",
    });
    const date = new Date(Date.UTC(2000, 0, 2, 0, 0, 0, 0));
    for (let i = 0; i < 12; i++) {
        date.setMonth(i);
        months.long[i] = df.format(date);
        months.short[i] = dfShort.format(date);
        months.narrow[i] = dfNarrow.format(date);
    }
    return months;
};
export const getDayLabels = (locale) => {
    const days = {
        long: ["", "", "", "", "", "", ""],
        short: ["", "", "", "", "", "", ""],
        narrow: ["", "", "", "", "", "", ""],
    };
    const df = Intl.DateTimeFormat(locale, { timeZone: "UTC", weekday: "long" });
    const dfShort = Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        weekday: "short",
    });
    const dfNarrow = Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        weekday: "narrow",
    });
    const date = new Date(Date.UTC(2000, 0, 2, 0, 0, 0, 0));
    for (let i = 2; i < 9; i++) {
        date.setDate(i);
        days.long[i - 2] = df.format(date);
        days.short[i - 2] = dfShort.format(date);
        days.narrow[i - 2] = dfNarrow.format(date);
    }
    return days;
};
/**
 * Returns all the various separators to use between the date and time
 * components.
 * <ul>
 * <li dateSeparator: use to separate the date parts. E.g. between year and month</li>
 * <li timeSeparator: use to separate the time parts. E.g. between hour and minute</li>
 * <li fractionalSeparator: use to separate the seconds from sub-second.</li>
 * <li dataTimeSeparator: use to separate the date part from the time part.</li>
 * <li dayPeriodSeparator: use to separate the period of the day from the time part.</li>
 * </ul>
 * @param locale THe locale to use
 * @returns  The temporal separators used by the given locale
 */
export const getTemporalSeparators = (locale) => {
    const parts = Intl.DateTimeFormat(locale, {
        timeZone: "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
    }).formatToParts(new Date("2000-12-22T23:57:34.999Z"));
    let dateSeparator = "-";
    let timeSeparator = ":";
    let fractionalSeparator = ".";
    let dataTimeSeparator = ", ";
    let dayPeriodSeparator = undefined;
    const dateFirst = parts[0].type === "year" ||
        parts[0].type === "month" ||
        parts[0].type === "day";
    if (dateFirst) {
        dateSeparator = parts[1].value;
        let i = 0;
        while (i < parts.length && parts[i].type != "hour") {
            i++;
        }
        if (i < parts.length - 5) {
            dataTimeSeparator = parts[i - 1].value;
            timeSeparator = parts[i + 1].value;
            fractionalSeparator = parts[i + 5].value;
        }
        if (i < parts.length - 7) {
            //no 24h clock
            dayPeriodSeparator = parts[i + 7].value;
        }
    }
    else {
        let i = 0;
        while (i < parts.length && parts[i].type != "hour") {
            i++;
        }
        if (i < parts.length - 1) {
            timeSeparator = parts[i + 1].value;
            fractionalSeparator = parts[i + 5].value;
        }
        if (parts[i + 8].type === "dayPeriod") {
            //no 24h clock
            dayPeriodSeparator = parts[i + 7].value;
        }
        while (i < parts.length &&
            parts[i].type !== "year" &&
            parts[i].type !== "month" &&
            parts[i].type !== "day") {
            i++;
        }
        if (i < parts.length - 1) {
            dataTimeSeparator = parts[i - 1].value;
            dateSeparator = parts[i + 1].value;
        }
    }
    return {
        dateSeparator,
        dataTimeSeparator,
        timeSeparator,
        fractionalSeparator,
        dayPeriodSeparator,
    };
};
export var DateTimePart;
(function (DateTimePart) {
    DateTimePart[DateTimePart["MILLIS"] = 1] = "MILLIS";
    DateTimePart[DateTimePart["SECOND"] = 2] = "SECOND";
    DateTimePart[DateTimePart["MINUTE"] = 4] = "MINUTE";
    DateTimePart[DateTimePart["HOUR"] = 8] = "HOUR";
    DateTimePart[DateTimePart["DAY"] = 16] = "DAY";
    DateTimePart[DateTimePart["WEEK"] = 32] = "WEEK";
    DateTimePart[DateTimePart["MONTH"] = 64] = "MONTH";
    DateTimePart[DateTimePart["QUARTER"] = 128] = "QUARTER";
    DateTimePart[DateTimePart["YEAR"] = 256] = "YEAR";
})(DateTimePart || (DateTimePart = {}));
;
const DateTimePart_DATE = DateTimePart.YEAR + DateTimePart.MONTH + DateTimePart.DAY;
export const isIntlDateTime = (options) => {
    if (typeof options === "object") {
        return options.quarter === undefined && options.week === undefined;
    }
    else {
        return (options & (DateTimePart.QUARTER + DateTimePart.WEEK)) === 0;
    }
};
export const parseFormatOptions = (format) => {
    const _format = format.toLowerCase().trim();
    const formatParser = new RegExp("^(date[.]hour|date[.]minute|date[.]second|date[.]millis|year|quarter|month|week|day|hour|minute|second|millis)(30|20|15|12|10|6|5|4|3|2|1|)?$", "gmi");
    formatParser.lastIndex = 0;
    const matches = formatParser.exec(format);
    let mask = 0; // when mask is/stays zero, the given format is illegal.
    let precision = undefined;
    if (matches) {
        const componentPrecision = matches[1];
        const subComponentPrecision = matches[2];
        switch (componentPrecision) {
            case "year":
                mask = DateTimePart.YEAR;
                break;
            case "quarter":
                mask = DateTimePart.YEAR + DateTimePart.QUARTER;
                break;
            case "month":
                mask = DateTimePart.YEAR + DateTimePart.MONTH;
                break;
            case "week":
                mask = DateTimePart.YEAR + DateTimePart.WEEK;
                break;
            case "day":
                mask = DateTimePart_DATE;
                break;
            case "date.hour":
                mask = DateTimePart_DATE + DateTimePart.HOUR;
                break;
            case "date.minute":
                mask = DateTimePart_DATE + DateTimePart.HOUR + DateTimePart.MINUTE;
                break;
            case "date.second":
                mask = DateTimePart_DATE + DateTimePart.HOUR + DateTimePart.MINUTE + DateTimePart.SECOND;
                break;
            case "date.millis":
                mask = DateTimePart_DATE + DateTimePart.HOUR + DateTimePart.MINUTE + DateTimePart.SECOND + DateTimePart.MILLIS;
                break;
            case "hour":
                mask = DateTimePart.HOUR;
                break;
            case "minute":
                mask = DateTimePart.HOUR + DateTimePart.MINUTE;
                break;
            case "second":
                mask = DateTimePart.HOUR + DateTimePart.MINUTE + DateTimePart.SECOND;
                break;
            case "millis":
                mask = DateTimePart.HOUR + DateTimePart.MINUTE + DateTimePart.SECOND + DateTimePart.MILLIS;
                break;
        }
        if ((mask & DateTimePart.MILLIS) > 0) {
            //we end in millis
            if (subComponentPrecision) {
                const p = parseInt(subComponentPrecision);
                if (p <= 3) {
                    //3 digits is highest precision allowed
                    precision = p;
                }
                else {
                    mask = 0;
                }
            }
            else {
                precision = 3;
            }
        }
        else if ((mask & (DateTimePart.MINUTE + DateTimePart.SECOND)) > 0) {
            //we end in seconds or minutes
            precision = subComponentPrecision
                ? parseInt(subComponentPrecision)
                : 1;
        }
        else if (subComponentPrecision) {
            //we end in a higher order componentPrecision
            //these componentPrecisions do not allow for sub-component precision;
            mask = 0;
        }
    }
    const result = { mask, precision };
    if (mask === 0) {
        console.warn(`DateTimeFormat ${format} is not correct.`);
    }
    return result;
};
/**
 * Returns the Intl date time format options for format specification.
 * @param format format specifier.Flag indicating that the date part will be used. Defaults to true.
 * @returns The Intl.DateTimeFormatOptions.
 */
export const getIntlDateTimeFormatOptions = (format) => {
    const config = parseFormatOptions(format);
    const options = {};
    let precision = undefined;
    if (isIntlDateTime(config.mask)) {
        options.year = config.mask & DateTimePart.YEAR ? "numeric" : undefined;
        options.month = config.mask & DateTimePart.MONTH ? "2-digit" : undefined;
        options.day = config.mask & DateTimePart.DAY ? "2-digit" : undefined;
        options.hour = config.mask & DateTimePart.HOUR ? "2-digit" : undefined;
        options.minute = config.mask & DateTimePart.MINUTE ? "2-digit" : undefined;
        options.second = config.mask & DateTimePart.SECOND ? "2-digit" : undefined;
        options.fractionalSecondDigits =
            config.mask & 1 ? config.precision : undefined;
        if ((config.mask & 6) > 0) {
            precision = config.precision;
        }
    }
    else {
        options.year = config.mask & DateTimePart.YEAR ? "numeric" : undefined;
        options.quarter = config.mask & DateTimePart.QUARTER ? "numeric" : undefined;
        options.week = config.mask & DateTimePart.WEEK ? "2-digit" : undefined;
    }
    return { options, precision, mask: config.mask };
};
export const getTestDateTimeFormatParts = (locale, options) => {
    Object.assign({}, options, { timeZone: "UTC" });
    return Intl.DateTimeFormat(locale, Object.assign({}, options, { timeZone: "UTC" })).formatToParts(new Date("2000-12-22T23:57:34.999Z"));
};
/**
 * Returns a date where the least significant component is snapped according to
 * the given formatting options. The least significant component is either
 * either the minute, second or millisecond.
 * @param date the date where to snap the least significant component for.
 * @param formatOptions The formatting options.
 * @returns A new date snapped according to the formatting options
 */
const snapTime = (date, formatOptions) => {
    const snappedDate = new Date(date.getTime());
    if (formatOptions.precision != undefined) {
        if (formatOptions.options.second !== undefined) {
            //snap seconds to given precision
            snappedDate.setSeconds(Math.round(snappedDate.getSeconds() / formatOptions.precision) *
                formatOptions.precision);
            snappedDate.setMilliseconds(0);
        }
        else {
            //snap minutes to given precision
            snappedDate.setMinutes(Math.round(snappedDate.getMinutes() / formatOptions.precision) *
                formatOptions.precision);
            snappedDate.setSeconds(0);
            snappedDate.setMilliseconds(0);
        }
    }
    else if (formatOptions.options.fractionalSecondDigits != undefined) {
        //snap the milliseconds to given precision
        const p = Math.pow(10, formatOptions.options.fractionalSecondDigits);
        snappedDate.setMilliseconds(Math.round(snappedDate.getMilliseconds() / p) * p);
    }
    return snappedDate;
};
const getSnapper = (formatOptions, system, operation = 0, part) => {
    const weekInterval = getWeekInterval(getFirstWeekDay(system));
    const snap = operation < 0
        ? (v) => Math.floor(v)
        : operation > 0
            ? (v) => Math.ceil(v)
            : (v) => Math.round(v);
    const msWeight = formatOptions.options.fractionalSecondDigits !== undefined
        ? Math.pow(10, formatOptions.options.fractionalSecondDigits)
        : 1;
    let snapper;
    const mask = part !== undefined ? part : formatOptions.mask;
    snapper = (d) => {
        let sd = new Date(d.getTime());
        if (mask & DateTimePart.MILLIS) { //formatOptions.options.fractionalSecondDigits !== undefined) {
            //snap to required millisecond interval
            sd.setMilliseconds(snap(sd.getMilliseconds() / msWeight) * msWeight);
        }
        else {
            //set milliseconds to 0
            const correctionSecond = snap(sd.getMilliseconds() / 1000);
            sd.setMilliseconds(0);
            sd.setSeconds(sd.getSeconds() + correctionSecond);
            if (mask & DateTimePart.SECOND) { //formatOptions.options.second !== undefined) {
                //snap to required second interval
                if (formatOptions.precision !== undefined) {
                    sd.setSeconds(snap(sd.getSeconds() / formatOptions.precision) *
                        formatOptions.precision);
                }
            }
            else {
                //set second to 0
                const correctionMinute = snap(sd.getSeconds() / 60);
                sd.setSeconds(0);
                sd.setMinutes(sd.getMinutes() + correctionMinute);
                if (mask & DateTimePart.MINUTE) { //formatOptions.options.minute !== undefined) {
                    //snap to required minute interval
                    if (!formatOptions.options.second && formatOptions.precision !== undefined) {
                        sd.setMinutes(snap(sd.getMinutes() / formatOptions.precision) *
                            formatOptions.precision);
                    }
                }
                else {
                    //set minute to 0
                    const correctionHour = snap(sd.getMinutes() / 60);
                    sd.setMinutes(0);
                    sd.setHours(sd.getHours() + correctionHour);
                    if (mask & DateTimePart.HOUR) { //formatOptions.options.hour !== undefined) {
                        //at this point we snapped to an hour
                    }
                    else {
                        //set hour to 0
                        const correctionDay = snap(sd.getHours() / 24);
                        sd.setHours(0);
                        sd.setDate(sd.getDate() + correctionDay);
                        if (mask & DateTimePart.DAY) { //formatOptions.options.day !== undefined) {
                            //at this point we snapped to a day
                        }
                        else {
                            if (mask & DateTimePart.WEEK) { //formatOptions.options.week !== undefined) {
                                //snap to a week
                                const weekStart = weekInterval.floor(sd);
                                const correctionWeek = snap(d3.timeDay.count(weekStart, sd) / 7);
                                sd = weekInterval.offset(weekStart, correctionWeek);
                            }
                            else {
                                //set day to 1
                                const dim = getDaysInMonth(sd);
                                const correctionMonth = snap((sd.getDate() - 1) / dim);
                                sd.setDate(1);
                                sd.setMonth(sd.getMonth() + correctionMonth);
                                if (mask & DateTimePart.MONTH) { //formatOptions.options.month !== undefined) {
                                    //at this point we snapped to a month
                                }
                                else {
                                    if (mask & DateTimePart.QUARTER) { //formatOptions.options.quarter !== undefined) {
                                        //snap to a quarter
                                        sd.setMonth((snap(sd.getMonth() / 3) % 4) * 3);
                                    }
                                    else {
                                        //snap to a year
                                        //set month to 0
                                        const dim = getDaysInMonth(sd);
                                        const correctionYear = snap(sd.getMonth() / 12);
                                        sd.setMonth(0);
                                        sd.setFullYear(sd.getFullYear() + correctionYear);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return sd;
    };
    return snapper;
};
export const getValidFactory = (start, end, formatOptions, system) => {
    const floor = getSnapper(formatOptions, system, -1);
    const ceil = getSnapper(formatOptions, system, 1);
    const interval = getInterval(formatOptions, system);
    //If start or end is a number, it means the number of intervals from floor(now)
    const makeValid = (date) => {
        const now = floor(new Date());
        const _start = start === undefined
            ? undefined
            : start.getTimezoneOffset
                ? floor(start).getTime()
                : interval.offset(now, start).getTime();
        const _end = end === undefined
            ? undefined
            : end.getTimezoneOffset
                ? ceil(end).getTime()
                : interval.offset(now, end).getTime();
        //_max is the maximum value (1 interval before _end; _end is exclusive)
        const _max = _end != undefined ? interval.offset(new Date(_end), -1) : undefined;
        if (_start !== undefined &&
            (date === undefined || date.getTime() < _start)) {
            return new Date(_start);
        }
        if (_end !== undefined && (date === undefined || date.getTime() >= _end)) {
            return _max;
        }
        //flooring the date leeds to the least amount of confusion (contrary the round/snap which can pull a date towards the future )
        return floor(new Date(date.getTime()));
    };
    return makeValid;
};
export const getValidator = (start, end, formatOptions, system, part) => {
    const mask = part !== undefined ? part : formatOptions.mask;
    const floor = getSnapper(formatOptions, system, -1, mask);
    const ceil = getSnapper(formatOptions, system, 1);
    const interval = getInterval(formatOptions, system);
    const leastSignificantPart = getDateTimePrecision(formatOptions);
    const validateStart = (start, date) => {
        return (start === undefined ||
            (date !== undefined && date.getTime() >= start));
    };
    const validateEnd = leastSignificantPart === part
        ? (end, date) => {
            return (end === undefined ||
                (date !== undefined && date.getTime() < end));
        }
        : (end, date) => {
            return (end === undefined ||
                (date !== undefined && date.getTime() <= end));
        };
    const validate = (date) => {
        const now = new Date();
        const _start = start === undefined
            ? undefined
            : start.getTimezoneOffset
                ? floor(start).getTime()
                : floor(interval.offset(now, start)).getTime();
        const _end = end === undefined
            ? undefined
            : end.getTimezoneOffset
                ? ceil(end).getTime()
                : floor(interval.offset(now, end)).getTime();
        //_max is the maximum value (1 interval before _end; _end is exclusive)
        const _max = _end != undefined ? interval.offset(new Date(_end), -1) : undefined;
        return validateStart(_start, date) && validateEnd(_end, date);
    };
    return validate;
};
/**
 * Returns a placeholder string for what kind of date time format is expected
 * @param locale The locale where to create a placeholder text for
 * @param options  The Intl.DateTimeFormatOptions to use for creating the place holder text.
 * @returns A place holder string.
 */
export const getPlaceHolder = (locale, formatOptions) => {
    let parts = [];
    if (isIntlDateTime(formatOptions.options)) {
        parts = getTestDateTimeFormatParts(locale, formatOptions.options);
        const localeUses12HourClock = parts.filter((part) => part.type === "dayPeriod").length > 0;
        for (let i = 0; i < parts.length; i++) {
            if (parts[i].type === "year")
                parts[i].value = "yyyy";
            if (parts[i].type === "month")
                parts[i].value = "MM";
            if (parts[i].type === "day")
                parts[i].value = "dd";
            if (parts[i].type === "hour")
                parts[i].value = localeUses12HourClock ? "hh" : "HH";
            if (parts[i].type === "minute")
                parts[i].value = "mm";
            if (parts[i].type === "second")
                parts[i].value = "ss";
            if (parts[i].type === "fractionalSecond")
                parts[i].value = "SSS";
            if (parts[i].type === "dayPeriod")
                parts[i].value = "aa";
        }
        //  return parts.map((part) => part.value).join("");
    }
    else if (formatOptions.options.year &&
        (formatOptions.options.quarter || formatOptions.options.week)) {
        parts = getTestDateTimeFormatParts(locale, {
            year: "numeric",
            month: "2-digit",
        });
        for (let i = 0; i < parts.length; i++) {
            if (parts[i].type === "year")
                parts[i].value = "yyyy";
            if (parts[i].type === "month")
                parts[i].value = formatOptions.options.quarter
                    ? "q"
                    : formatOptions.options.week === "2-digit"
                        ? "ww"
                        : "w";
        }
    }
    return parts.map((part) => part.value).join("");
};
/**
 * Returns a localized Date formatter
 * @param locale Which locale to create a date formatter for.
 * @param options  The Intl.DateTimeFormatOptions to use for creating the formatter.
 * @returns Date object if parsing was successful. Undefined otherwise.
 */
export const getFormatter = (locale, system, formatOptions) => {
    if (isIntlDateTime(formatOptions.options)) {
        const f = Intl.DateTimeFormat(locale, formatOptions.options);
        return (date) => {
            //years before 1000 or after 9999 are not handled correctly by the formatter so perform this trick.
            const formatDate = snapTime(date, formatOptions);
            formatDate.setFullYear(1970);
            const formatted = f.format(formatDate);
            return formatted.replace("1970", date.getFullYear().toString());
        };
    }
    else if (formatOptions.options.year &&
        (formatOptions.options.quarter || formatOptions.options.week)) {
        const parts = getTestDateTimeFormatParts(locale, {
            year: "numeric",
            month: "2-digit",
        });
        if (formatOptions.options.quarter) {
            return (date) => {
                return parts
                    .map((part) => {
                    if (part.type === "year") {
                        return date.getFullYear().toString();
                    }
                    else if (part.type === "month") {
                        return (Math.floor(date.getMonth() / 3) + 1).toString();
                    }
                    else if (part.type === "literal") {
                        return part.value;
                    }
                    else {
                        return "";
                    }
                })
                    .join("");
            };
        }
        else {
            return (date) => {
                const weekNr = getWeekOther(system, date);
                return parts
                    .map((part) => {
                    if (part.type === "year") {
                        return weekNr.year.toString();
                    }
                    else if (part.type === "month") {
                        return ((formatOptions.options.week === "2-digit" && weekNr.week < 10
                            ? "0"
                            : "") + weekNr.week.toString());
                    }
                    else if (part.type === "literal") {
                        return part.value;
                    }
                    else {
                        return "";
                    }
                })
                    .join("");
            };
        }
    }
    else {
        return (date) => date.toISOString();
    }
};
/**
 * Returns a localized parser for date time strings
 * @param locale Which locale to create a date time parser for.
 * @param options  The Intl.DateTimeFormatOptions to use for creating the parser.
 * @returns Date object if parsing was successful. Undefined otherwise.
 */
export const getParser = (locale, system, formatOptions
//  output: "Date" | "string" | "number" = "string"
//): ((t: string) => Date | string | number | undefined) => {
) => {
    if (isIntlDateTime(formatOptions.options)) {
        const parts = getTestDateTimeFormatParts(locale, formatOptions.options);
        const localeUses12HourClock = parts.filter((part) => part.type === "dayPeriod").length > 0;
        const dayPeriodLabels = localeUses12HourClock
            ? getPeriodLabels(locale)
            : undefined;
        const dateTimePartTypes = parts.map((part) => part.type);
        const datePartTypes = dateTimePartTypes.filter((partType) => partType === "year" || partType === "month" || partType === "day");
        const yearIndex = datePartTypes.indexOf("year");
        const monthIndex = datePartTypes.indexOf("month");
        const dayIndex = datePartTypes.indexOf("day");
        const timeStartIndex = datePartTypes.length > 0 ? 3 + datePartTypes.length : 2;
        const timePartTypes = dateTimePartTypes.filter((partType) => partType === "hour" ||
            partType === "minute" ||
            partType === "second" ||
            partType === "fractionalSecond" ||
            partType === "dayPeriod");
        const startWithPeriod = timePartTypes.indexOf("dayPeriod") === 0;
        const timeAnyExpression = "(2[0-3]|[01][0-9]|[0-9])(?:\\D+([0-5][0-9]|[0-9])(?:\\D+([0-5][0-9]|[0-9])(?:[,.]([0-9][0-9][0-9]|[0-9][0-9]|[0-9]))?)?)?(\\D+|$)";
        const dateTimeMatchMap = {
            year: yearIndex != -1 ? yearIndex + 2 : undefined,
            month: monthIndex != -1 ? monthIndex + 2 : undefined,
            day: dayIndex != -1 ? dayIndex + 2 : undefined,
            hour: timeStartIndex + 1,
            minute: timeStartIndex + 2,
            second: timeStartIndex + 3,
            fractionalSecond: timeStartIndex + 4,
            dayPeriodPre: timeStartIndex + 0,
            dayPeriodPost: timeStartIndex + 5,
        };
        let dateTimeRegexp = "";
        //date part
        if (datePartTypes.length > 0) {
            dateTimeRegexp += "(";
            for (let i = 0; i < datePartTypes.length; i++) {
                if (i > 0) {
                    dateTimeRegexp += "\\D+?";
                }
                switch (datePartTypes[i]) {
                    case "year":
                        dateTimeRegexp += "([-]?\\d+)";
                        break;
                    case "month":
                        dateTimeRegexp += "(1[012]|0[1-9]|[1-9])";
                        break;
                    case "day":
                        dateTimeRegexp += "(3[01]|[12][0-9]|0[1-9]|[1-9])";
                        break;
                }
            }
            dateTimeRegexp += ")";
        }
        //time part
        if (datePartTypes.length > 0) {
            //time is optional when we also want (full)dates
            if (datePartTypes.length === 3) {
                dateTimeRegexp += `((\\D+\\s*)${timeAnyExpression})?(?:\D+|$)`;
            }
            else {
                dateTimeRegexp += "(?:D+|$)";
            }
        }
        else {
            //time only
            dateTimeRegexp += `((\\D+\\s*)?${timeAnyExpression})`;
        }
        //console.log('dateTimeRegexp',dateTimeRegexp);
        const dateTimeValidator = new RegExp(dateTimeRegexp, "gmi");
        //helper to try to figure out the period of day
        const getPeriodIndex = (periodMatch, searchPM) => {
            let index = -1;
            if (periodMatch && periodMatch.trim().length > 0) {
                index = dayPeriodLabels
                    ? periodMatch.indexOf(dayPeriodLabels[searchPM ? 1 : 0].toUpperCase())
                    : -1;
                if (index == -1) {
                    index = periodMatch.indexOf(searchPM ? "PM" : "AM");
                }
            }
            return index;
        };
        //helper to try to figure out the period of day
        const getPeriod = (periodMatch) => {
            const amIndex = getPeriodIndex(periodMatch, false);
            const pmIndex = getPeriodIndex(periodMatch, true);
            if (pmIndex > -1 || amIndex > -1) {
                if (pmIndex == -1) {
                    return 0;
                }
                else if (amIndex == -1) {
                    return 12;
                }
                else {
                    return amIndex < pmIndex ? 0 : 12;
                }
            }
            return -1;
        };
        //helper to try to figure out the period of day
        const usePM = (pre, post) => {
            let prePeriod = getPeriod(pre);
            let postPeriod = getPeriod(post);
            if (prePeriod > -1 || postPeriod > -1) {
                if (prePeriod == -1) {
                    return postPeriod == 12;
                }
                else if (postPeriod == -1) {
                    return prePeriod == 12;
                }
                else {
                    return startWithPeriod ? prePeriod == 12 : postPeriod == 12;
                }
            }
            return false;
        };
        const parser = (str) => {
            dateTimeValidator.lastIndex = 0;
            const dateTimeMatches = dateTimeValidator.exec(str);
            //    console.log('str',str);
            //    console.log('dateTimeMatches',dateTimeMatches);
            if (dateTimeMatches) {
                let hour = dateTimePartTypes.indexOf("hour") != -1 &&
                    dateTimeMatches[dateTimeMatchMap.hour]
                    ? parseInt(dateTimeMatches[dateTimeMatchMap.hour])
                    : 0;
                const minute = dateTimePartTypes.indexOf("minute") != -1 &&
                    dateTimeMatches[dateTimeMatchMap.minute]
                    ? parseInt(dateTimeMatches[dateTimeMatchMap.minute])
                    : 0;
                const second = dateTimePartTypes.indexOf("second") != -1 &&
                    dateTimeMatches[dateTimeMatchMap.second]
                    ? parseInt(dateTimeMatches[dateTimeMatchMap.second])
                    : 0;
                const fractionalSecond = dateTimePartTypes.indexOf("fractionalSecond") != -1 &&
                    dateTimeMatches[dateTimeMatchMap.fractionalSecond]
                    ? parseInt(dateTimeMatches[dateTimeMatchMap.fractionalSecond])
                    : 0;
                const dayPeriodPre = dateTimePartTypes.indexOf("hour") != -1
                    ? dateTimeMatches[dateTimeMatchMap.dayPeriodPre]?.toUpperCase()
                    : undefined;
                const dayPeriodPost = dateTimePartTypes.indexOf("hour") != -1
                    ? dateTimeMatches[dateTimeMatchMap.dayPeriodPost]?.toUpperCase()
                    : undefined;
                if (!!dayPeriodPre || !!dayPeriodPost) {
                    if (hour >= 1 && hour <= 12) {
                        //only make corrections if the given hour is within range of the 12 hour clock
                        if (hour === 12) {
                            hour = 0;
                        }
                        if (usePM(dayPeriodPre, dayPeriodPost)) {
                            hour += 12;
                        }
                    }
                }
                const year = dateTimeMatchMap.year && dateTimeMatches[dateTimeMatchMap.year]
                    ? parseInt(dateTimeMatches[dateTimeMatchMap.year])
                    : 1970;
                const month = dateTimeMatchMap.month && dateTimeMatches[dateTimeMatchMap.month]
                    ? parseInt(dateTimeMatches[dateTimeMatchMap.month]) - 1
                    : 0;
                const day = dateTimeMatchMap.day && dateTimeMatches[dateTimeMatchMap.day]
                    ? parseInt(dateTimeMatches[dateTimeMatchMap.day])
                    : 1;
                const date = new Date(0);
                date.setFullYear(year);
                date.setMonth(month);
                date.setDate(day);
                date.setHours(hour);
                date.setMinutes(minute);
                date.setSeconds(second);
                date.setMilliseconds(fractionalSecond);
                // const result = snapTime(date, formatOptions);
                // switch (output) {
                //   case "string":
                //     return result.toISOString();
                //   case "number":
                //     return result.getTime();
                // }
                return date;
            }
            else {
                return undefined;
            }
        };
        return parser;
    }
    else {
        //week or quarter
        const parts = getTestDateTimeFormatParts(locale, {
            year: "numeric",
            month: "2-digit",
        });
        const datePartTypes = parts
            .map((part) => part.type)
            .filter((partType) => partType === "year" || partType === "month");
        const yearIndex = datePartTypes.indexOf("year");
        const quarterOrWeekIndex = datePartTypes.indexOf("month");
        let dateTimeRegexp = "";
        dateTimeRegexp += "(";
        for (let i = 0; i < datePartTypes.length; i++) {
            if (i > 0) {
                dateTimeRegexp += "\\D+?";
            }
            switch (datePartTypes[i]) {
                case "year":
                    dateTimeRegexp += "([-]?\\d+)";
                    break;
                case "month":
                    dateTimeRegexp += formatOptions.options.quarter
                        ? "([1234])"
                        : "(5[0123]|[1234][0-9]|0[1-9]|[1-9])";
                    break;
            }
        }
        dateTimeRegexp += ")";
        let toDate;
        if (formatOptions.options.quarter) {
            toDate = (year, quarter) => {
                const date = new Date();
                date.setFullYear(year);
                date.setMonth((quarter - 1) * 3);
                date.setDate(1);
                date.setHours(0);
                date.setMinutes(0);
                date.setSeconds(0);
                date.setMilliseconds(0);
                return date;
            };
        }
        else {
            toDate = (year, week) => {
                const date = getFirstWeekStart(system, year).date;
                date.setDate(date.getDate() + (week - 1) * 7);
                return date;
            };
        }
        const dateTimeValidator = new RegExp(dateTimeRegexp, "gmi");
        const parser = (str) => {
            dateTimeValidator.lastIndex = 0;
            const dateTimeMatches = dateTimeValidator.exec(str);
            //    console.log('str',str);
            //    console.log('dateTimeMatches',dateTimeMatches);
            if (dateTimeMatches) {
                const year = parseInt(dateTimeMatches[yearIndex + 2]);
                const quarterOrWeek = parseInt(dateTimeMatches[quarterOrWeekIndex + 2]);
                const result = toDate(year, quarterOrWeek);
                // switch (output) {
                //   case "string":
                //     return result.toISOString();
                //   case "number":
                //     return result.getTime();
                // }
                return result;
            }
            else {
                return undefined;
            }
        };
        return parser;
    }
};
const escapeRegExp = (str) => {
    return str.replace(/[.*+?^${}()\/|[\]\\]/g, "\\$&"); // $& means the whole matched string
};
const isDateSeparator = (i, parts) => {
    const prev = parts[i - 1].type;
    const next = parts[i + 1].type;
    return ((prev === "year" || prev === "month" || prev === "day") &&
        (next === "year" || next === "month" || next === "day"));
};
const isTimeSeparator = (i, parts) => {
    const prev = parts[i - 1].type;
    const next = parts[i + 1].type;
    return ((prev === "hour" || prev === "minute" || prev === "second") &&
        (next === "hour" || next === "minute" || next === "second"));
};
const isDateTimeSeparator = (i, parts) => {
    const prev = parts[i - 1].type;
    const next = parts[i + 1].type;
    return ((prev === "year" || prev === "month" || prev === "day") &&
        (next === "hour" || next === "minute" || next === "second"));
};
const isFractionalSeparator = (i, parts) => {
    const prev = parts[i - 1].type;
    const next = parts[i + 1].type;
    return prev === "second" && next === "fractionalSecond";
};
const isDayPeriodSeparator = (i, parts) => {
    const next = parts[i + 1].type;
    return next === "dayPeriod";
};
export const getDayIndices = (weekStart = 0) => {
    let dayIndices = [];
    for (let i = 0; i < 7; i++) {
        dayIndices[i] = (i + weekStart) % 7;
    }
    return dayIndices;
};
export const getMonthIndices = (firstMonth = 0) => {
    let monthIndices = [];
    for (let i = 0; i < 12; i++) {
        monthIndices[i] = (i + firstMonth) % 12;
    }
    return monthIndices;
};
export const getMonth = (date) => {
    return date.getMonth();
};
export const getQuarter = (date) => {
    return (Math.floor(date.getMonth() / 3) * 3);
};
export const getWeekInterval = (weekStart) => {
    switch (weekStart) {
        case 0:
            return d3.timeSunday;
        case 1:
            return d3.timeMonday;
        case 2:
            return d3.timeTuesday;
        case 3:
            return d3.timeWednesday;
        case 4:
            return d3.timeThursday;
        case 5:
            return d3.timeFriday;
        case 6:
            return d3.timeSaturday;
    }
};
export const getDateTimePrecision = (formatOptions) => {
    if (formatOptions.mask & DateTimePart.MILLIS) { //formatOptions.options.fractionalSecondDigits !== undefined) {
        return DateTimePart.MILLIS;
    }
    else if (formatOptions.mask & DateTimePart.SECOND) { //formatOptions.options.second !== undefined) {
        return DateTimePart.SECOND;
    }
    else if (formatOptions.mask & DateTimePart.MINUTE) { //formatOptions.options.minute !== undefined) {
        return DateTimePart.MINUTE;
    }
    else if (formatOptions.mask & DateTimePart.HOUR) { //formatOptions.options.hour !== undefined) {
        return DateTimePart.HOUR;
    }
    else if (formatOptions.mask & DateTimePart.DAY) { //formatOptions.options.day !== undefined) {
        return DateTimePart.DAY;
    }
    else if (formatOptions.mask & DateTimePart.WEEK) { //formatOptions.options.week !== undefined) {
        return DateTimePart.WEEK;
    }
    else if (formatOptions.mask & DateTimePart.MONTH) { //formatOptions.options.month !== undefined) {
        return DateTimePart.MONTH;
    }
    else if (formatOptions.mask & DateTimePart.QUARTER) { //formatOptions.options.quarter !== undefined) {
        return DateTimePart.QUARTER;
    }
    else if (formatOptions.mask & DateTimePart.YEAR) { //formatOptions.options.year !== undefined) {
        return DateTimePart.YEAR;
    }
    return DateTimePart.MINUTE;
};
export const getCountableInterval = (formatOptions, system, part) => {
    const mask = part !== undefined ? part : formatOptions.mask;
    if (mask & DateTimePart.MILLIS) { //formatOptions.options.fractionalSecondDigits !== undefined) {
        return d3.timeMillisecond;
    }
    else if (mask & DateTimePart.SECOND) { //formatOptions.options.second !== undefined) {
        return d3.timeSecond;
    }
    else if (mask & DateTimePart.MINUTE) { //formatOptions.options.minute !== undefined) {
        return d3.timeMinute;
    }
    else if (mask & DateTimePart.HOUR) { //formatOptions.options.hour !== undefined) {
        return d3.timeHour;
    }
    else if (mask & DateTimePart.DAY) { //formatOptions.options.day !== undefined) {
        return d3.timeDay;
    }
    else if (mask & DateTimePart.WEEK) { //formatOptions.options.week !== undefined) {
        return getWeekInterval(getFirstWeekDay(system));
    }
    else if (mask & DateTimePart.MONTH) { //formatOptions.options.month !== undefined) {
        return d3.timeMonth;
    }
    else if (mask & DateTimePart.QUARTER) { //formatOptions.options.quarter !== undefined) {
        return d3.timeMonth;
    }
    else if (mask & DateTimePart.YEAR) { //formatOptions.options.year !== undefined) {
        return d3.timeYear;
    }
    return d3.timeMinute;
};
export const getInterval = (formatOptions, system, part) => {
    const mask = part !== undefined ? part : formatOptions.mask;
    const interval = getCountableInterval(formatOptions, system, mask);
    let offset = interval.offset;
    if (mask & DateTimePart.MILLIS) { //formatOptions.options.fractionalSecondDigits !== undefined) {
        if (formatOptions.options.fractionalSecondDigits < 3) {
            const every = Math.pow(10, 3 - formatOptions.options.fractionalSecondDigits);
            //return <d3.TimeInterval>interval.every(every); //not countable
            const msWeight = Math.pow(10, formatOptions.options.fractionalSecondDigits);
            interval.offset = (date, step) => {
                const od = new Date(date.getTime());
                od.setMilliseconds((Math.floor(od.getMilliseconds() / msWeight) * msWeight) + (step ?? 1) * every);
                return od;
            };
        }
        return interval;
    }
    else if (mask & DateTimePart.SECOND) { //formatOptions.options.second !== undefined) {
        if (formatOptions.precision !== undefined && formatOptions.precision > 1) {
            //return <d3.TimeInterval>interval.every(formatOptions.precision); //not countable
            interval.offset = (date, step) => {
                const od = new Date(date.getTime());
                od.setSeconds((Math.floor(od.getSeconds() / formatOptions.precision) *
                    formatOptions.precision) + (step ?? 1) * formatOptions.precision);
                od.setMilliseconds(0);
                return od;
            };
        }
        return interval;
    }
    else if (mask & DateTimePart.MINUTE) { //formatOptions.options.minute !== undefined) {
        if (!formatOptions.options.second && formatOptions.precision !== undefined && formatOptions.precision > 1) {
            //return <d3.TimeInterval>interval.every(formatOptions.precision); //not countable
            interval.offset = (date, step) => {
                const od = new Date(date.getTime());
                od.setMinutes((Math.floor(od.getMinutes() / formatOptions.precision) *
                    formatOptions.precision) + (step ?? 1) * formatOptions.precision);
                od.setSeconds(0);
                od.setMilliseconds(0);
                return od;
            };
        }
        return interval;
    }
    else if (mask & DateTimePart.HOUR) { //formatOptions.options.hour !== undefined) {
        return interval;
    }
    else if (mask & DateTimePart.DAY) { //formatOptions.options.day !== undefined) {
        return interval;
    }
    else if (mask & DateTimePart.WEEK) { //formatOptions.options.week !== undefined) {
        return interval;
    }
    else if (mask & DateTimePart.MONTH) { //formatOptions.options.month !== undefined) {
        return interval;
    }
    else if (mask & DateTimePart.QUARTER) { //formatOptions.options.quarter !== undefined) {
        //    return <d3.TimeInterval>interval.every(3); //not countable
        interval.offset = (date, step) => {
            const od = new Date(date.getTime());
            od.setDate(1);
            od.setMonth((Math.floor(od.getMonth() / 3) * 3) + (step ?? 1) * 4);
            od.setHours(0);
            od.setMinutes(0);
            od.setSeconds(0);
            od.setMilliseconds(0);
            return od;
        };
        return interval;
    }
    else if (mask & DateTimePart.YEAR) { //formatOptions.options.year !== undefined) {
        return interval;
    }
    return interval;
    //interval.offset(now, <number>end)
};
export const isLeapYear = (year) => {
    return getDaysInYear(year) > 365;
};
export const getDaysInYear = (year) => {
    let yr = year.getFullYear ? year.getFullYear() : year;
    let date = new Date();
    date.setTime(0);
    date.setFullYear(yr);
    date.setMonth(0);
    date.setDate(1);
    let millisStart = date.getTime();
    date.setFullYear(yr + 1);
    let millisEnd = date.getTime();
    return (millisEnd - millisStart) / (24 * 3600000);
};
const monthInDays = [
    [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31],
    [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31],
];
const monthStartingDays = [
    [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334],
    [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335],
];
export const getDaysInMonth = (year, month) => {
    let yr = year.getFullYear ? year.getFullYear() : year;
    let mnth = year.getFullYear ? year.getMonth() : month;
    return monthInDays[isLeapYear(yr) ? 1 : 0][mnth];
};
/**
 * return 1..365 for common years and 1..366 for leap years
 */
export const getDayOfTheYear = (date) => {
    return (monthStartingDays[isLeapYear(date) ? 1 : 0][date.getMonth()] +
        date.getDate());
};
/**
 * Calculate on which day the year ends
 * 0=sunday
 */
const lastDayOfTheYear = (year) => {
    let yr = year.getFullYear ? year.getFullYear() : year;
    //   https://en.wikipedia.org/wiki/ISO_week_date; western traditional/javascript  day numbering
    //	return (yr + Math.floor(yr / 4) - Math.floor(yr / 100) + Math.floor(yr / 400)) % 7;
    let date = new Date();
    date.setTime(0);
    date.setFullYear(yr);
    date.setMonth(11);
    date.setDate(31);
    return date.getDay();
};
/**
 * Calculate on which day the year starts
 * 0=sunday
 */
const firstDayOfTheYear = (year) => {
    let yr = year.getFullYear ? year.getFullYear() : year;
    let date = new Date();
    date.setTime(0);
    date.setFullYear(yr);
    date.setMonth(0);
    date.setDate(1);
    return date.getDay();
};
// export const getWeekCount = (year: number | Date): number => {
// 	let yr = (<Date>year).getFullYear ? (<Date>year).getFullYear() : <number>year;
// 	return lastDayOfTheYear(yr) == 4 || lastDayOfTheYear(yr - 1) == 3 ? 53 : 52;
// };
export var WeekNumberSystem;
(function (WeekNumberSystem) {
    WeekNumberSystem["ISO8601"] = "iso8601";
    WeekNumberSystem["WESTERN_TRADITIONAL"] = "western_traditional";
    WeekNumberSystem["MIDDLE_EASTERN"] = "middle_eastern";
    WeekNumberSystem["BROADCAST_CALENDAR"] = "broadcast_calendar";
})(WeekNumberSystem || (WeekNumberSystem = {}));
export const DATE_SYSTEM = {
    iso8601: {
        sunday: 6, //monday=0
        firstWeekDay: 1, //(from JS point of view)
        startDay: 1,
        startMonth: 0, //0=January
        minimumDaysLeftOnStart: 4,
    },
    western_traditional: {
        sunday: 0, //sunday=0
        firstWeekDay: 0, //(from JS point of view)
        startDay: 1,
        startMonth: 0, //0=January
        minimumDaysLeftOnStart: 1,
    },
    middle_eastern: {
        sunday: 1, //saturday=0
        firstWeekDay: 6, //(from JS point of view)
        startDay: 1,
        startMonth: 0, //0=January
        minimumDaysLeftOnStart: 1,
    },
    broadcast_calendar: {
        sunday: 6, //monday=0
        firstWeekDay: 1, //(from JS point of view)
        startDay: 1,
        startMonth: 0, //0=January
        minimumDaysLeftOnStart: 1,
    },
};
// export const getDay = (date: Date, system?: WeekNumberSystem): number => {
// 	const day = date.getDay();
// 	//default to Javascript , i.e. WESTERN_TRADITIONAL
// 	const sys = system ? system : WeekNumberSystem.WESTERN_TRADITIONAL;
// 	const dayNumberForSunday = DATE_SYSTEM[sys].sunday;
// 	return (day + dayNumberForSunday) % 7;
// };
// /**
//  * Calculate the ISO8601 week number
//  * https://en.wikipedia.org/wiki/ISO_week_date
//  * 0=sunday
//  */
// export const getWeek = (date: Date): WeekNumber => {
// 	const year = date.getFullYear();
// 	const doy = getDayOfTheYear(date);
// 	const dow = getDay(date, WeekNumberSystem.ISO8601) + 1; // (1=monday, 7=sunday)
// 	const week = Math.floor((10 + doy - dow) / 7);
// 	const system = WeekNumberSystem.ISO8601;
// 	return Object.assign(
// 		{ system: system },
// 		week < 1
// 			? { year: year - 1, week: getWeekCount(year - 1) }
// 			: week > getWeekCount(year)
// 			? { year: year + 1, week: 1 }
// 			: { year: year, week: week }
// 	);
// };
// /**
//  * calculates the start date of given ISO8601 week number
//  */
// export const getWeekStart = (year: number | WeekNumber, week?: number): Date => {
// 	const yr = <number>(
// 		((<WeekNumber>year).week != undefined ? (<WeekNumber>year).year : <number>year)
// 	);
// 	const wk = <number>((<WeekNumber>year).week != undefined ? (<WeekNumber>year).week : week);
// 	//get january 1 for given year (local time)
// 	let date = new Date();
// 	date.setTime(0); //reset first to EPOCH
// 	date.setFullYear(yr);
// 	date.setMonth(0);
// 	date.setDate(1);
// 	date.setHours(0);
// 	date.setMinutes(0);
// 	date.setSeconds(0);
// 	date.setMilliseconds(0);
// 	//calculate the offset (in days) of the start of the  first week of the given year relative to the start of the year
// 	const dayStartWeekYearOffset = 3 - ((date.getDay() + 2) % 7);
// 	//calculate the offset of the given week relative to the first week of the year
// 	const weekOffset = (wk - 1) * 7;
// 	//calculate the start of the given week
// 	date.setDate(date.getDate() + dayStartWeekYearOffset + weekOffset);
// 	return date;
// };
export const getFirstWeekDay = (system) => {
    const isCustom = system.firstWeekDay !== undefined;
    const dateSystem = isCustom
        ? system
        : DATE_SYSTEM[system];
    return dateSystem.firstWeekDay;
};
export const getFirstMonth = (system) => {
    const isCustom = system.firstWeekDay !== undefined;
    const dateSystem = isCustom
        ? system
        : DATE_SYSTEM[system];
    return dateSystem.startMonth;
};
export const getWeekOther = (system, date) => {
    const isCustom = system.firstWeekDay !== undefined;
    const dateSystem = isCustom
        ? system
        : DATE_SYSTEM[system];
    let dayStart = d3.timeDay.floor(date);
    let year = dayStart.getFullYear();
    let startFirstWeek = getFirstWeekStart(dateSystem, year);
    if (startFirstWeek.date.getTime() <= dayStart.getTime()) {
        let startNextYear = getFirstWeekStart(dateSystem, year + 1);
        if (startNextYear.date.getTime() > dayStart.getTime()) {
            //the week number lies in same year as given date
            startFirstWeek = startFirstWeek;
        }
        else {
            //the week number lies in year following the year of given date
            startFirstWeek = startNextYear;
        }
    }
    else {
        //the week number lies in year preceding the year of given date
        startFirstWeek = getFirstWeekStart(dateSystem, year - 1);
    }
    const offset = d3.timeDay.count(startFirstWeek.date, dayStart);
    const week = Math.floor(offset / 7);
    const weekOffset = week * 7;
    startFirstWeek.date.setDate(startFirstWeek.date.getDate() + weekOffset);
    return {
        system: isCustom ? "custom" : system,
        year: startFirstWeek.year,
        week: week + 1,
        start: startFirstWeek.date,
    };
};
export const getWeekStartOther = (system, year, week) => {
    const isCustom = system.firstWeekDay !== undefined;
    const dateSystem = isCustom
        ? system
        : DATE_SYSTEM[system];
    const yr = ((year.week != undefined
        ? year.year
        : year));
    const wk = ((year.week != undefined ? year.week : week));
    const date = getFirstWeekStart(dateSystem, yr).date;
    const weekOffset = (wk - 1) * 7;
    date.setDate(date.getDate() + weekOffset);
    return date;
};
export const getFirstWeekStart = (system, year) => {
    const isCustom = system.firstWeekDay !== undefined;
    const dateSystem = isCustom
        ? system
        : DATE_SYSTEM[system];
    let yr = year.getFullYear ? year.getFullYear() : year;
    let startOfTheYear = new Date(yr, 0, 1);
    let startFirstWeek = new Date(yr, dateSystem.startMonth, dateSystem.startDay);
    let startDayInFirstWeek = startFirstWeek.getDay(); //0=sunday
    const dayNumberInStartWeek = (startDayInFirstWeek - dateSystem.firstWeekDay) % 7;
    const daysLeftOnStart = 7 - dayNumberInStartWeek;
    const deltaDaysFromStart = (daysLeftOnStart >= dateSystem.minimumDaysLeftOnStart ? 0 : 7) -
        dayNumberInStartWeek;
    startFirstWeek.setDate(startFirstWeek.getDate() + deltaDaysFromStart);
    return {
        year: yr,
        dayOfTheYear: (startFirstWeek.getTime() - startOfTheYear.getTime()) / (24 * 3600000),
        date: startFirstWeek,
    };
};
export const isSameDate = (d0, d1) => {
    return (d0 !== undefined &&
        d1 !== undefined &&
        d0.getFullYear() === d1.getFullYear() &&
        d0.getMonth() === d1.getMonth() &&
        d0.getDate() === d1.getDate());
};
export const isBetween = (d, range) => {
    return (d !== undefined &&
        range[0] !== undefined &&
        range[1] !== undefined &&
        d.getTime() >= range[0].getTime() &&
        d.getTime() < range[1].getTime());
};
